<template>
  <BaseCard class="mb-6">
    <div class="flex space-x-2">
      <ButtonUpload
        v-if="organizationConfig?.productImportsEnabled"
        :is-disabled="!canUpload"
        @upload="onUpload"
      >
        Import Products
      </ButtonUpload>

      <ButtonExport
        :is-loading="isExporting"
        :default-file-export-format="
          tenant.defaultExportFiletype
        "
        @export="onExport"
      >
        Export Products
      </ButtonExport>
    </div>
  </BaseCard>

  <BaseCard class="relative flex flex-col overflow-hidden">
    <template v-if="latestImport?.status === 'finished'">
      <p class="mb-4 text-xl font-medium text-positive/87">
        Last import successful at
        {{ formatToTime(latestImport.importedAt) }} on
        {{ formatToDate(latestImport.importedAt) }}
      </p>
    </template>
    <template
      v-if="
        !latestImport || latestImport.status === 'finished'
      "
    >
      <div class="min-h-24 overflow-y-auto">
        <p
          class="shrink-0 text-sm leading-loose text-background-fg/70"
        >
          If this is your first time importing products,
          please export first to download a template
          spreadsheet
          <br />
          Categories can be nested by separating the
          category name by " > "
          <br />
          To delete a product, mark an 'x' in the delete
          column
          <br />
          After importing products, you will be presented
          with a preview of the changes made by the import.
          Products not shown in the preview will be
          unchanged.
          <br />
          To edit a product's colour, please use one of the
          following colour names:
        </p>
        <ul class="mt-1 space-y-1">
          <template
            v-for="color in useColorOptions().options"
            :key="color.value"
          >
            <li class="flex space-x-2">
              <ColorItem
                rounded
                :value="`rgb(var(${color.value}))`"
              />
              <span class="text-sm font-medium">
                {{ color.label.split(" ").join("-") }}
              </span>
            </li>
          </template>
        </ul>
      </div>
    </template>
    <template v-else>
      <p
        class="shrink-0 text-sm leading-loose text-background-fg/70"
      >
        <template
          v-if="
            latestImport.status === 'dr_finished' &&
            !latestImport.rowErrors
          "
        >
          Please review this import thoroughly and confirm
          when ready.
        </template>
        <template
          v-else-if="
            latestImport.status === 'dr_started' ||
            latestImport.status === 'dr_pending'
          "
        >
          Waiting for import result...
        </template>
        <template
          v-else-if="
            latestImport.status === 'started' ||
            latestImport.status === 'pending'
          "
        >
          Importing products...
        </template>
      </p>
    </template>

    <template v-if="latestImport?.status === 'dr_finished'">
      <template v-if="latestImport.rowErrors">
        <p class="shrink-0 leading-loose text-error">
          Import failed. Please check the spreadsheet.
        </p>

        <div
          v-if="latestImport.data.rowErrors"
          class="mt-4 h-full overflow-y-auto"
        >
          <p
            >The following rows could not be imported due to
            errors:</p
          >
          <div
            v-for="row in latestImport.data.rowErrors"
            :key="row.number"
            class=""
          >
            <span class="font-semibold"
              >Row #{{ row.number + 1 }}</span
            >
            <div
              v-for="(error, index) in row.errors"
              :key="index"
              class="ml-4"
            >
              <span class="font-semibold"
                >{{ error.loc[0] }}:
              </span>
              <span> {{ error.msg }}</span>
            </div>
          </div>
        </div>
      </template>
      <div class="my-4 flex shrink-0 justify-between">
        <button
          type="button"
          class="relative inline-flex cursor-pointer items-center rounded-lg border border-primary bg-background px-3 py-1 text-sm font-medium text-primary hover:bg-primary/3 focus:z-10 focus:outline-none"
          @click="onCancelImport"
        >
          Cancel Import
        </button>
        <button
          v-if="latestImport.rowErrors === false"
          type="button"
          class="relative inline-flex cursor-pointer items-center rounded-lg border border-background bg-primary px-3 py-1 text-sm font-medium text-primary-fg hover:bg-primary/70 focus:z-10 focus:outline-none"
          @click="onAcceptImport"
        >
          Confirm Import
        </button>
      </div>

      <template
        v-if="latestImport.data && !latestImport.rowErrors"
      >
        <div class="h-full overflow-y-auto">
          <ProductImportTable
            :rows="latestImport?.data.rows"
          />
        </div>
      </template>
      <template v-if="isSubmitting">
        <BaseInnerLoading class="" />
      </template>
      <template v-if="isPending">
        <span
          class="text-xl font-medium text-background-fg/87"
        >
          Waiting for import result...
        </span>
      </template>
    </template>
  </BaseCard>
</template>

<script setup lang="ts">
import { ApiError } from "@/api"
import { queryClient } from "@/queryClient"
import type { FileExportFormat } from "@/composables/options/useFileExportFormatOptions"

const onUpload = async (file: File) => {
  try {
    await createImport(file)
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.code === "invalid-file-format"
    ) {
      toast.error("Invalid file format uploaded.")
    } else if (
      error instanceof ApiError &&
      error.code === "invalid-file-encoding"
    ) {
      toast.error(
        "Invalid file encoding used. Please make sure your file uses ascii or utf-8."
      )
    } else if (
      error instanceof ApiError &&
      error.code === "product-imports-disabled"
    ) {
      toast.error("Product imports are disabled")
    } else {
      toast.error("Somthing went wrong")
    }
  }
}

const isExporting = ref(false)
const onExport = async (
  fileExtension?: FileExportFormat
) => {
  if (isExporting.value) return
  isExporting.value = true
  try {
    await exportProducts(fileExtension)
  } finally {
    isExporting.value = false
  }
}

const { data: latestImport, refetch: refetchLatestImport } =
  useLatestImport()
const { tenant } = useTenant()
const { organizationConfig } = useOrganizationConfig()

const isPending = computed(() =>
  latestImport.value
    ? ["dr_started", "dr_pending"].includes(
        latestImport.value?.status
      )
    : false
)
const canUpload = computed(
  () =>
    !isSubmitting.value &&
    (latestImport.value?.status !== "dr_finished" ||
      latestImport.value.rowErrors)
)
const isSubmitting = ref(false)
const onAcceptImport = async () => {
  isSubmitting.value = true
  if (!latestImport.value) return
  try {
    await acceptImport(latestImport.value?.id)
    await refetchLatestImport()
  } catch (error) {
    logError(error)
    toast.error("Failed to accept import")
  } finally {
    isSubmitting.value = false
  }
}

const onCancelImport = async () => {
  isSubmitting.value = true
  if (!latestImport.value) return
  try {
    await cancelImport(latestImport.value?.id)
    await refetchLatestImport()
  } catch (error) {
    logError(error)
    toast.error("Failed to cancel import")
  } finally {
    isSubmitting.value = false
  }
}

whenever(
  () => latestImport.value?.status === "finished",
  () => {
    queryClient.invalidateQueries([BASE_PRODUCTS_QUERY_KEY])
    queryClient.invalidateQueries([
      BASE_CATEGORIES_QUERY_KEY,
    ])
  }
)
</script>
